import { useMemo, useState } from "react";
import { OrganizationCloseStage } from "@common/types/organizationCloseStage";
import { pluralizeWithCount } from "@common/helpers/string";
import { Button } from "@design-system/Button/Button";
import { CommandIsland } from "@design-system/CommandIsland/CommandIsland";
import { CommandIslandTotal } from "@design-system/CommandIsland/CommandIslandTotal";
import commandIslandStyles from "@design-system/CommandIsland/CommandIslandVariants.module.scss";
import { Shortcut } from "@design-system/Shortcut/Shortcut";
import { Separator } from "@design-system/Separator/Separator";
import { BulkSelectionControl } from "@design-system/BulkSelectionControl/BulkSelectionControl";
import { Picker } from "@design-system/Picker/Picker.lib";
import type { PickerItemData } from "@design-system/Picker/Picker.types";
import { HStack } from "@design-system/HStack/HStack";
import { useCloseStageSelectFooter } from "./CloseStageSelectFooter";
import { Tooltip } from "@design-system/Tooltip/Tooltip";

interface Props {
    isSaving: boolean;
    isDirty: boolean;
    onSave: () => void | Promise<void>;
    onSaveAndApply: () => void | Promise<void>;
    onApply: () => void;
    onAddTask: () => void;
    onAddStage: (stageId: string) => void;
    hiddenStages: OrganizationCloseStage[];
    taskCount: number;
    // Bulk actions
    selectedCount: number;
    onDeselectRows: () => void;
    onOpenBulkActions: () => void;
}

export const CloseTemplateTasksTableFooter = ({
    isSaving,
    isDirty,
    onSave,
    onSaveAndApply,
    onApply,
    onAddTask,
    onAddStage,
    hiddenStages,
    taskCount,
    selectedCount,
    onDeselectRows,
    onOpenBulkActions,
}: Props) => {
    const [activeButton, setActiveButton] = useState<
        "save" | "saveAndApply" | null
    >(null);
    const renderStageFooter = useCloseStageSelectFooter();
    const hiddenStageItems = useMemo<Array<PickerItemData<string>>>(
        () =>
            hiddenStages.map((stage) => ({
                value: stage.id,
                label: stage.name,
            })),
        [hiddenStages],
    );

    const handleSave = async () => {
        setActiveButton("save");
        try {
            await onSave();
        } finally {
            setActiveButton(null);
        }
    };

    const handleSaveAndApply = async () => {
        setActiveButton("saveAndApply");
        try {
            await onSaveAndApply();
        } finally {
            setActiveButton(null);
        }
    };

    return (
        <CommandIsland
            sticky
            className={commandIslandStyles.bulkActionsStickyFooter}
        >
            <HStack gap={8}>
                {taskCount > 0 && (
                    <CommandIslandTotal>
                        {pluralizeWithCount("task", taskCount)}
                    </CommandIslandTotal>
                )}
                {selectedCount > 0 && (
                    <BulkSelectionControl
                        selectedCount={selectedCount}
                        onClearSelection={onDeselectRows}
                        size="md"
                    />
                )}
            </HStack>
            <HStack gap={8} className="ml-auto">
                {selectedCount > 0 && (
                    <>
                        <Tooltip content="Open bulk actions">
                            <Button
                                variant="primary"
                                size="md"
                                rightIcon={
                                    <span data-slot-box="">
                                        <Shortcut keys="mod+k" />
                                    </span>
                                }
                                onClick={onOpenBulkActions}
                            >
                                Actions
                            </Button>
                        </Tooltip>
                        <Separator orientation="vertical" />
                    </>
                )}
                <Picker.Select
                    items={hiddenStageItems}
                    value={null}
                    onValueChange={(stageId) => {
                        if (stageId !== null) {
                            onAddStage(stageId);
                        }
                    }}
                    placeholder="Add stage"
                    chevron="none"
                    emptyBehavior="hide"
                    renderFooter={renderStageFooter}
                />
                <Button variant="primary" size="md" onClick={onAddTask}>
                    Add task
                </Button>
                <Separator orientation="vertical" />
                {isDirty ? (
                    <>
                        <Button
                            variant="primary"
                            size="md"
                            onClick={handleSave}
                            disabled={isSaving}
                        >
                            {isSaving && activeButton === "save"
                                ? "Saving..."
                                : "Save"}
                        </Button>
                        {taskCount > 0 && (
                            <Button
                                variant="primary"
                                size="md"
                                onClick={handleSaveAndApply}
                                disabled={isSaving}
                            >
                                {isSaving && activeButton === "saveAndApply"
                                    ? "Saving..."
                                    : "Save and apply"}
                            </Button>
                        )}
                    </>
                ) : (
                    <Button
                        variant="primary"
                        size="md"
                        onClick={onApply}
                        disabled={taskCount === 0}
                    >
                        Apply
                    </Button>
                )}
            </HStack>
        </CommandIsland>
    );
};
